import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { usePresence } from '../../lib/api/realtime.js';
import type { PhonebookContact } from '@arcnet/contracts';

/**
 * Who the workspace can call (owner, 3 October 2026: "t9 dialing that resolves contacts and
 * extensions with states etc on transfers too"): the company's extensions, with what each is doing
 * now, and the phonebook the person may see -- the account's, their department's and their own.
 * One search over both, used by the dial pad and by transfers.
 */
export type ColleagueState = 'available' | 'ringing' | 'on_call' | 'dnd' | 'offline';
export interface Colleague {
  id: string; number: string; name: string; department: string | null;
  state: ColleagueState; since: string | null;
  /** One of their devices shows chat messages (browser phone, desktop or mobile app). */
  messaging?: boolean;
}
export interface Hit {
  key: string;
  kind: 'extension' | 'contact';
  number: string;
  name: string;
  /** The department, or the company and the kind of number. */
  sub: string | null;
  state?: ColleagueState;
}

export const STATE_LABEL: Record<ColleagueState, string> = {
  available: 'Available', ringing: 'Ringing', on_call: 'On a call', dnd: 'Do not disturb', offline: 'Offline',
};
export const STATE_TONE: Record<ColleagueState, 'ok' | 'warn' | 'info' | 'danger' | 'off'> = {
  available: 'ok', ringing: 'warn', on_call: 'info', dnd: 'danger', offline: 'off',
};

/**
 * Colleagues with their states, live (owner, 3 October 2026: "presence has to be realtime and
 * updating on events"). Who is in the directory -- names, numbers, departments -- is read once and
 * now and then; what each is doing arrives as it changes, from the same presence feed the ArcSIP
 * apps use (the portal's live connection, `presence` channel).
 */
export function useColleagues(tenantId: string | null, extensionId: string | null): { data: { data: Colleague[] } | undefined } {
  const q = useQuery({
    queryKey: ['colleagues', tenantId, extensionId],
    queryFn: () => api.get<{ data: Colleague[] }>(`/v1/tenants/${tenantId}/extensions/${extensionId}/colleagues`),
    enabled: !!tenantId && !!extensionId,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
  const live = usePresence(tenantId);
  const data = useMemo(() => q.data && {
    data: q.data.data.map((c) => {
      const p = live.get(c.number);
      return p ? { ...c, state: p.state, messaging: p.messaging,
        since: p.state === 'on_call' || p.state === 'ringing' ? new Date(p.since).toISOString() : null } : c;
    }),
  }, [q.data, live, live.size]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data };
}

/** The phonebooks this person sees, loaded once for searching as they type. */
export function useBookContacts(tenantId: string | null, extensionId: string | null, departmentId: string | null) {
  const get = (q: string) => api.get<{ data: PhonebookContact[] }>(`/v1/tenants/${tenantId}/phonebook/contacts?${q}&limit=1000`)
    .then((r) => r.data).catch(() => [] as PhonebookContact[]);
  return useQuery({
    queryKey: ['book-contacts', tenantId, extensionId, departmentId],
    queryFn: async () => {
      const lists = await Promise.all([
        get('book=account'),
        get(`book=personal&extension_id=${extensionId}`),
        ...(departmentId ? [get(`book=department&department_id=${departmentId}`)] : []),
      ]);
      return lists.flat();
    },
    enabled: !!tenantId && !!extensionId,
    staleTime: 5 * 60_000,
  });
}

const T9: Record<string, string> = {
  a: '2', b: '2', c: '2', d: '3', e: '3', f: '3', g: '4', h: '4', i: '4', j: '5', k: '5', l: '5',
  m: '6', n: '6', o: '6', p: '7', q: '7', r: '7', s: '7', t: '8', u: '8', v: '8', w: '9', x: '9', y: '9', z: '9',
};
/** A name as keypad digits, word by word: "Thandi Mokoena" -> ["842634", "6656362"]. */
const keypadWords = (name: string): string[] =>
  name.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).map((w) => [...w].map((ch) => T9[ch] ?? ch).join(''));
const digitsOf = (s: string) => s.replace(/\D/g, '');

/**
 * Best first: an extension number or a phone number that starts with what was typed, then a name
 * whose word starts with it (as letters, or as keypad digits), then anything that merely contains
 * it. Twelve at most: a list longer than that is a list nobody reads during a call.
 */
export function search(input: string, colleagues: Colleague[], contacts: PhonebookContact[]): Hit[] {
  const q = input.trim().toLowerCase();
  if (!q) return [];
  const digits = /^[0-9*#+\s]+$/.test(q) ? digitsOf(q) : '';
  const scored: Array<[number, Hit]> = [];
  const score = (name: string, numbers: string[]): number => {
    let best = 0;
    for (const n of numbers) {
      const d = digitsOf(n);
      if (digits && d.startsWith(digits)) best = Math.max(best, 4);
      else if (digits && digits.length >= 3 && d.includes(digits)) best = Math.max(best, 2);
    }
    if (digits) {
      if (keypadWords(name).some((w) => w.startsWith(digits))) best = Math.max(best, 3);
    } else {
      const words = name.toLowerCase().split(/\s+/);
      if (words.some((w) => w.startsWith(q))) best = Math.max(best, 3);
      else if (name.toLowerCase().includes(q)) best = Math.max(best, 1);
    }
    return best;
  };
  for (const c of colleagues) {
    const s = score(c.name, [c.number]);
    if (s) scored.push([s + 0.5, { key: `e${c.id}`, kind: 'extension', number: c.number, name: c.name, sub: c.department, state: c.state }]);
  }
  for (const c of contacts) {
    const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || '';
    for (const n of c.numbers) {
      const s = score(`${name} ${c.company_name ?? ''}`, [n.number]);
      if (s) scored.push([s, { key: `c${c.id}${n.number}`, kind: 'contact', number: n.number, name,
        sub: [c.company_name && c.company_name !== name ? c.company_name : null, n.kind].filter(Boolean).join(' · ') }]);
    }
  }
  return scored.sort((a, b) => b[0] - a[0] || a[1].name.localeCompare(b[1].name)).slice(0, 12).map(([, h]) => h);
}

/** The search, kept for one input. */
export function useSearch(input: string, colleagues: Colleague[] | undefined, contacts: PhonebookContact[] | undefined): Hit[] {
  return useMemo(() => search(input, colleagues ?? [], contacts ?? []), [input, colleagues, contacts]);
}
